import { useState, useEffect } from "react";
import {
  Drawer,
  Button,
  Input,
  Form,
  message,
  Typography,
  Tooltip,
  Row,
  Col,
  Image,
  Divider,
  Spin,
  Upload,
  Switch,
  InputNumber,
  DatePicker,
  Select,
  Radio,
} from "antd";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@apollo/client";
import {
  UPDATE_COUPON,
  GET_GROUPS_COUPONS,
  GET_PRODUCTS_VARIANTS,
} from "./requests";
import { ProductVariant } from "../../../../types/product";
import { couponTypes } from "../../../../types";
import useUpload from "../../../../hooks/use-upload";
import { renewalOptions } from "../../../../utils/constants";
import CreateGroupCoupon from "../createGroupModal";
import FormProduct from "../formProduct";
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CouponDrawerProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  coupon: typeof couponTypes.coupon | undefined;
  refetch: () => void;
}

const EditCoupon = ({
  showModal,
  setShowModal,
  coupon,
  refetch,
}: CouponDrawerProps) => {
  const [form] = Form.useForm<typeof couponTypes.coupon>();
  const { upload } = useUpload();
  const [loading, setLoading] = useState(false);
  const [uploadLoding, setUploadLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groups, setGroups] = useState<typeof couponTypes.groupCoupon[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [typeCoupon, setTypeCoupon] = useState<string | null>(null);
  const [privateType, setPrivateType] = useState(true);
  const [code, setCode] = useState<string | null>("");
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState<number | null>(null);
  const [minPurchase, setMinPurchase] = useState<number | null>(null);
  const [maxDiscount, setMaxDiscount] = useState<number | null>(null);
  const [totalPrice, setTotalPrice] = useState<number | null>(null);
  const [giftVariants, setGiftVariants] = useState<ProductVariant[]>([]);
  const [minQuantity, setMinQuantity] = useState<number | null>(null);
  const [oneTimeUse, setOneTimeUse] = useState(false);
  const [localPayload, setLocalPayload] = useState<any>({
    product: {
      name: "coupon",
      description: "",
      categories: [],
      rulesAdditionals: {
        requireComplements: false,
        maxComplements: 0,
        complementItems: [],
        requireBeverages: false,
        maxBeverages: 0,
        beverageItems: [],
        includesExtras: false,
        extraRules: [],
      },
    },
    productVariants: {
      price: 0,
      img: null,
    },
  });

  const { data: variantsData, loading: loadingVariants } = useQuery(GET_PRODUCTS_VARIANTS, {
    skip: !showModal || typeCoupon !== 'gift',
    variables: {
      params: { page: 1,
        pageSize: 100 },
    },
  });
  const [isSaveProductData, setIsSaveProductData] = useState(false);

  const { data: groupsCoupons, refetch: refetchGroups } = useQuery(GET_GROUPS_COUPONS, {
    skip: !showModal,
    variables: {
      params: { page: 1,
        pageSize: 100 },
    },
  });

  useEffect(()=>{
    if(groupsCoupons && groupsCoupons?.groupsCoupon) {
      setGroups(groupsCoupons?.groupsCoupon?.results ?? [])
    }
  }, [groupsCoupons, showModal])

  useEffect(() => {
    if (coupon) {
      console.log('CUPON A EDITAR:', coupon);
      form.setFieldsValue({
        name: coupon.name,
        conditions: coupon.conditions,
        comparePrice: coupon.comparePrice,
        expires: dayjs(coupon.expires),
        totalPrice: coupon.totalPrice,
        renewal: coupon.renewal,
        limitPerClient: coupon.limitPerClient,
        description: coupon.description,
      });
      setImageUrl(coupon.img || null);
      setSelectedGroups(coupon.groups.map((group) => group.id));
      setTypeCoupon(coupon.type);
      setPrivateType(coupon.privateType);
      setCode(coupon.code);
      setTotalPrice(coupon.totalPrice);
      setDiscountType(coupon.discountType);
      setDiscountValue(coupon.discount);
      setMinPurchase(coupon.minPurchase);
      setMinQuantity(coupon.minQuantity);
      setMaxDiscount(coupon.maxDiscount);

      setGiftVariants(coupon.giftVariants.map(variant => variant.id) as any);
      setOneTimeUse(coupon.oneTimeUse ?? false);
      setLocalPayload((prev: any) => ({
        ...prev,
        productCoupon: Array.isArray(coupon.productCoupon)
          ? coupon.productCoupon.map((p: any) => p.id)
          : coupon.productCoupon?.id
            ? [coupon.productCoupon.id]
            : [],
        minQuantity: coupon.minQuantity ?? null,
      }));
    }
  }, [coupon, showModal]);

  const [editCoupon] = useMutation(UPDATE_COUPON);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();

      // Validación especial para tipo 'gift'
      if (typeCoupon === 'gift') {
        if (!giftVariants || giftVariants.length === 0) {
          message.error("Debes seleccionar al menos una variante de producto para regalar");
          setLoading(false);
          return;
        }
        if (!minQuantity && !minPurchase) {
          message.error("Debes configurar al menos 'Cantidad mínima' o 'Compra mínima'");
          setLoading(false);
          return;
        }
      }

      let payload = {};
      if (typeCoupon === "product") {
        payload = {
          id: coupon?.id,
          coupon: {
            ...values,
            img: imageUrl,
            groups: selectedGroups,
            type: typeCoupon,
            privateType,
            productCoupon: coupon?.productCoupon.id,
          },
          product: localPayload.product,
          productVariants: localPayload.productVariants,
        };
      } else if (typeCoupon === "gift") {
        payload = {
          id: coupon?.id,
          coupon: {
            ...values,
            img: imageUrl,
            groups: selectedGroups,
            type: typeCoupon,
            privateType,
            giftVariants: giftVariants,
            minQuantity: minQuantity,
            minPurchase: minPurchase,
            oneTimeUse: oneTimeUse,
          },
        };
      } else {
        payload = {
          id: coupon?.id,
          coupon: {
            ...values,
            img: imageUrl,
            groups: selectedGroups,
            type: typeCoupon,
            code: code,
            privateType,
            discountType,
            discount: discountValue,
            minPurchase: minPurchase,
            maxDiscount: maxDiscount,
          },
        };
      }

      await editCoupon({ variables: payload });
      await refetch();

      message.success("Cupón editado exitosamente");
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al editar el cupón");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `coupons/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen agregada.");
  };

  const onClose = () => {
    setShowModal(false);
    form.resetFields();
    setImageUrl(null);
    setSelectedGroups([])
    setGroups([])
    setCode("");
    setTypeCoupon(null);
    setOneTimeUse(false);
  }

  return (
    <Drawer
      title="Editar Cupón"
      open={showModal}
      onClose={onClose}
      width={"60%"}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Nombre"
          rules={[{ required: true }]}
        >
          <Input placeholder="Ingrese el nombre" />
        </Form.Item>
        <Form.Item
          name="description"
          label="Descripción"
          rules={[{ required: true }]}
        >
          <TextArea placeholder="Ingrese la descripción" />
        </Form.Item>
        <Form.Item
          name="conditions"
          label="Condiciones"
          rules={[{ required: true }]}
        >
          <TextArea placeholder="Ingrese las condiciones" />
        </Form.Item>
        <Row>
          <Col span={12}>
            <Form.Item
              name="expires"
              label="Fecha de expiración"
              rules={[{ required: true }]}
            >
              <DatePicker
                disabledDate={(current) => current && current < dayjs().endOf("day")}
                showTime
                format="DD-MM-YYYY HH:mm"
                placeholder="Seleccione la fecha de expiración"
                style={{ width: "95%",
                  marginRight: 5 }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="renewal"
              label="Renovación"
              rules={[{ required: true }]}
            >
              <Select style={{ width: "95%" }} placeholder="Seleccionar renovación">
                {Object.keys(renewalOptions).map((key) => (
                  <Option key={key} value={key}>
                    {renewalOptions[key].name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={8}>
            <Form.Item
              name="comparePrice"
              label="Precio Comparativo"
              rules={[{ required: true,
                type: "number",
                min: 0 }]}
            >
              <InputNumber
                style={{ width: "95%",
                  marginRight: 5 }}
                min={0}
                placeholder="Ingrese el precio comparativo"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="totalPrice"
              label="Precio Total"
              rules={[{ required: true,
                type: "number",
                min: 0 }]}
            >
              <InputNumber
                style={{ width: "95%",
                  marginRight: 5 }}
                min={0}
                placeholder="Ingrese el precio total"
                onChange={(value) => setTotalPrice(value ?? 0)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="limitPerClient"
              label="Límite por cliente"
            >
              <InputNumber
                style={{ width: "95%" }}
                min={0}
                placeholder="Dejar vacío para sin límite"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Row style={{ marginBottom: 15 }}>
        <Text style={{ marginRight: 10 }}>Grupo</Text>
        <Select
          mode="multiple"
          value={selectedGroups}
          style={{ width: "85%" }}
          placeholder="Seleccionar grupos"
          onChange={(value) => setSelectedGroups(value)}
          showSearch
          filterOption={(input, option) => {
            const groupName = typeof option?.children === 'string' ? option.children : '';
            return groupName.toLowerCase().includes(input.toLowerCase());
          }}
        >
          {groups.map(({ id, group }) => (
            <Select.Option key={id} value={id}>
              {group}
            </Select.Option>
          ))}
        </Select>
        <Tooltip title="Agregar nuevo grupo">
          <Button
            type="primary"
            style={{ marginLeft: 10,
              borderRadius: 10 }}
            icon={<PlusOutlined />}
            onClick={() => setShowGroupModal(true)}
          />
        </Tooltip>
      </Row>
      <Text>Cambiar Imagen</Text>
      <Upload
        beforeUpload={() => false}
        listType="picture"
        maxCount={1}
        accept="image/*"
        onChange={(info) => {
          handleFileUpload(info);
        }}
        showUploadList={false}
      >
        <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
        {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
      </Upload>
      {imageUrl && (
        <Image style={{ marginLeft: 20 }} width={140} src={imageUrl} />
      )}
      <Divider />

      <Form.Item
        label="Privado"
        style={{ marginTop: 10 }}
      >
        <Switch
          onChange={(e) => setPrivateType(e)}
          value={privateType}
        >
        </Switch>
      </Form.Item>
      {typeCoupon === "code" && (
        <>
          <Form.Item label="Código">
            <Input
              value={code ?? ""}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Ingrese el código"
            />
          </Form.Item>
          <Form.Item style={{ marginTop: 10 }}>
            <Radio.Group
              onChange={(e) => setDiscountType(e.target.value)}
              value={discountType}
            >
              <Radio.Button value="percentage">Porcentaje</Radio.Button>
              <Radio.Button value="fixed">Cantidad Fija</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Descuento">
            {discountType === "percentage" ? (
              <InputNumber
                min={0}
                max={100}
                addonBefore="%"
                onChange={(value) => setDiscountValue(value)}
                style={{ width: "100%" }}
                value={discountValue}
              />
            ) : (
              <InputNumber
                min={0}
                onChange={(value) => setDiscountValue(value)}
                style={{ width: "100%" }}
                value={discountValue}
              />
            )}
          </Form.Item>
          <Form.Item label="Compra mínima">
            <InputNumber
              min={0}
              onChange={(value) => setMinPurchase(value)}
              style={{ width: "100%" }}
              placeholder="Monto mínimo de compra"
              value={minPurchase}
            />
          </Form.Item>
          <Form.Item label="Descuento máximo">
            <InputNumber
              min={0}
              onChange={(value) => setMaxDiscount(value)}
              style={{ width: "100%" }}
              placeholder="Tope máximo del descuento"
              value={maxDiscount}
            />
          </Form.Item>
        </>
      )}
      {typeCoupon === "gift" && (
        <div
          style={{
            border: '1px solid #eee',
            borderRadius: 8,
            padding: 20,
            marginBottom: 24,
          }}
        >
          <Typography.Title level={5}>Configuración de Regalo</Typography.Title>
          <Form.Item label={<span>Variantes a regalar <span style={{ color: 'red' }}>*</span></span>} required>
            <Select
              mode="multiple"
              showSearch
              placeholder="Selecciona las variantes que se regalarán al cliente"
              loading={loadingVariants}
              value={giftVariants}
              onChange={(value) => setGiftVariants(value)}
              style={{ width: '100%' }}
              optionFilterProp="children"
              filterOption={(input, option) => {
                const label = (option?.children ?? '') as string;
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {variantsData?.productVariants?.results?.map((variant) => (
                <Select.Option key={variant.id} value={variant.id}>
                  {variant.product.name === variant.title ? variant.title : `${variant.product.name} - ${variant.title}`}
                </Select.Option>
              ))}
            </Select>
            <div style={{ fontSize: 12,
              color: '#888' }}>
              Selecciona las variantes que se regalarán al cliente
            </div>
          </Form.Item>
          <Divider />
          <Typography.Text strong>Condiciones (al menos una requerida):</Typography.Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Cantidad mínima de productos">
                <InputNumber
                  min={1}
                  value={minQuantity ?? undefined}
                  onChange={(value) => setMinQuantity(value)}
                  style={{ width: '100%' }}
                  placeholder="Cantidad mínima de productos en el carrito"
                />
                <div style={{ fontSize: 12,
                  color: '#888' }}>
                  El cliente debe tener esta cantidad de productos en su carrito (sin contar el regalo)
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Compra mínima (MXN)">
                <InputNumber
                  min={0}
                  value={minPurchase ?? undefined}
                  onChange={(value) => setMinPurchase(value)}
                  style={{ width: '100%' }}
                  placeholder="Monto mínimo de compra para obtener el regalo"
                />
                <div style={{ fontSize: 12,
                  color: '#888' }}>
                  El total de la compra debe ser igual o mayor a esta cantidad (después de descuentos)
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <Form.Item label="Uso único (solo una vez por cliente)">
            <Switch
              checked={oneTimeUse}
              onChange={(checked) => setOneTimeUse(checked)}
            />
            <div style={{ fontSize: 12,
              color: '#888',
              marginTop: 4 }}>
              Cuando está activado, cada cliente solo puede usar este cupón una vez en su vida (ideal para promociones de bienvenida)
            </div>
          </Form.Item>
        </div>
      )}
      {typeCoupon === "product" && (
        <FormProduct
          isCreateView={false}
          setIsSaveProductData={setIsSaveProductData}
          totalPrice={totalPrice}
          comparePrice={form.getFieldValue('comparePrice')}
          imageUrl={imageUrl}
          setPayload={setLocalPayload}
          product={coupon?.productCoupon}
          refetch={refetch}
        />
      )}
      <Divider />
      <Button
        size="large"
        loading={loading}
        type="primary"
        onClick={handleSubmit}
        style={{ marginTop: 20 }}
        disabled={
          (!isSaveProductData && typeCoupon === "product") ||
          (typeCoupon === "code" && !privateType && !imageUrl)
        }
      >
        Actualizar Cupón
      </Button>
      <CreateGroupCoupon
        showGroupModal={showGroupModal}
        setShowGroupModal={setShowGroupModal}
        refetch={refetchGroups}
        isCreateView={false}
        selectedGroups={selectedGroups}
        setSelectedGroups={setSelectedGroups}
      />
    </Drawer>
  );
};

export default EditCoupon;
